import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import __ from "lodash";
import moment from "moment/moment";
import MaterialTable from "@material-table/core";
import { defaultComponents, MaterialTableBody, MaterialTableBodyRow } from '../../../controls/Material-Table-Components';
import { v4 as uuidv4 } from 'uuid';

import SystemContext from "../../../SystemContext";

import {ColumnWidth, ValidatorColumn} from "../../../blocks/ColumnHelper";
import {useFlightManifests, useAddFlightManifest, useDeleteFlightManifest, useUpdateFlightManifest} from "../../../services/flightManifests";
import FlightManifestUlds from "./FlightManifestUlds";
import {
  ManifestClosed, ManifestOpen,
} from "../../../icons";
import {MaskedNumber} from "../../../controls";
import { useTheme } from '@material-ui/core';


const dateFormat = "DD/MM/YYYY HH:mm";

const FlightManifestViewer = (_props) => {
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const tableRef = createRef({state: {lastEditingRow: false}});
  const {isAuthenticated} = useAuth0();
  const eventBus = SystemData.eventBus;

  const props = {
    ..._props
  }
  const {classes, extraKeys} = props;

  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const handleAddDeadLoad = useAddFlightManifest();
  const handleUpdateDeadLoad = useUpdateFlightManifest();
  const handleDeleteDeadLoad = useDeleteFlightManifest();

  const queryClient = useQueryClient();
  const manifests = useFlightManifests({queryClient, extraKeys});

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: state.canEdit?"always":"never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    //const validatorColumn = ValidatorColumn(DeadloadEditorValidator, false);

    return [
      //{...validatorColumn},
      {
        ...baseColumn,
        ...ColumnWidth(60,2,undefined,undefined,{width: 60, paddingLeft: 40, maxWidth: 60},undefined,false),
        title: "Loading Point",
        field: "airportOfLoading",
        //defaultGroupOrder: 0
      },
      {
        ...baseColumn,
        ...ColumnWidth(60,2,undefined,undefined,{width: 60, paddingLeft: 40, maxWidth: 60},undefined,false),
        title: "Offload Point",
        field: "airportOfUnloading",
        editable: "never",
      },
      {
        ...baseColumn,
        ...ColumnWidth(80,2,undefined,undefined,{width: 80, paddingLeft: 40, maxWidth: 80},undefined,false),
        title: "Source",
        field: "manifestSource",
      },
      {
        ...baseColumn,
        ...ColumnWidth(130,2,undefined,undefined,{width: 130, paddingLeft: 40, maxWidth: 130},undefined,false),
        title: "Received",
        field: "manifestTime",
        render: (rowData) => (
          <div>{moment.utc(rowData.manifestTime).format(dateFormat)}</div>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(45,2,undefined,undefined,{width: 45, paddingLeft: 40, maxWidth: 45},undefined,false),
        title: "Parts",
        field: "manifestNumber",
      },
      {
        ...baseColumn,
        ...ColumnWidth(45,2,undefined,undefined,{width: 45, paddingLeft: 40, maxWidth: 45},undefined,false),
        title: "ULD Count",
        field: "uldCount",
        // type: "numeric"
      },
      {
        ...baseColumn,
        ...ColumnWidth(90,2,undefined,undefined,{width: 90, paddingLeft: 60, maxWidth: 90},undefined,false),
        title: "Weight",
        field: "totalWeight",
        type: "numeric",
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.totalWeight}
            decimalScale={0}
            fixedDecimalScale={true}
            fullWidth
            suffix={"Kg"}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        )
      },
      // {
      //   ...baseColumn,
      //   ...ColumnWidth(150,2,undefined,undefined,{width: 150, paddingLeft: 40, maxWidth: 150},undefined,false),
      //   title: "Special Handling",
      //   field: "shcs",
      // },
      {
        ...baseColumn,
        ...ColumnWidth(10,2,undefined,undefined,undefined,undefined,false),
        title: "",
        field: "partsMissing",
        render: (rowData) => ``
      },
      {
        ...baseColumn,
        ...ColumnWidth(450,2,undefined,undefined,{width: 450, paddingLeft: 40, maxWidth: 450},undefined,false),
        title: "Remarks",
        field: "partsMissing",
        render: (rowData) => `${Number(rowData.partsMissing) > 0 ? `${rowData.partsMissing} manifest parts were not received. ${rowData.manifestRemarks ?? ""}` : rowData.manifestRemarks ?? ""}`
      },
    ];
  };


  const bodyRef = createRef();
  SystemData.toggleLoadingProgress(true, "Loading Flight Manifests ...");
  if (manifests && manifests.data)
    SystemData.toggleLoadingProgress(false);

  return (isAuthenticated &&
        <div className={classes.TripLegEditor} style={{width: "100%", overflow: "scroll", maxHeight: 700}}>
          <MaterialTable
            tableRef={tableRef}
            data={manifests.data}
            style={{
              backgroundColor: "#fdfdfd",
              boxShadow: "none",
              border: "2px solid #aaaaaa",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              search: false,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.childTableHeader.backgroundColor,
                color: theme.palette.childTableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              detailPanelType: "single",
              showTitle: false,
              toolbar: false,
              padding: 'dense',
              paging: false,
              filtering: false,
              draggable: false,
              grouping: false
            }}
            components={{
              ...defaultComponents,
              Body: (props) => (
                <MaterialTableBody
                  {...props}
                  ref={bodyRef}
                  rowHeight={46}
                  overscanRowCount={10}
                  addVirtualScrolling={true}
                  bodyHeight={600}
                  headerHeight={46}
                />
              ),
              Row: (props) => (
                <MaterialTableBodyRow
                  {...props}
                />
              ),
            }}
            title="Flight Manifest"
            columns={getColumns({...props, canEdit: state.canEdit})}
            detailPanel={
              [
                {
                  tooltip: "Show Manifest ULDs",
                  icon: () => (<ManifestClosed />),
                  openIcon: () => (<ManifestOpen />),
                  render: (rowData) => {
                    return (
                      <FlightManifestUlds
                        canEdit={state.canEdit}
                        classes={classes}
                        extraKeys={[rowData.manifestUuid]}
                      />
                    );
                  },
                },
              ]
            }
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                ? (newData) => new Promise((resolve, reject) => {
                    handleAddDeadLoad.mutate({record: newData, resolve, reject, extraKeys});
                  })
                : undefined,
              onRowUpdate: state.canEdit
                ?
                  (newData, oldData) =>
                  new Promise((resolve, reject) => {
                    handleUpdateDeadLoad.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
                  })
                : undefined,
              onRowDelete: state.canEdit
                ? (oldData) =>
                  new Promise((resolve, reject) => {
                    handleDeleteDeadLoad.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                  })
                : undefined,
            }}
        />
      </div>
  );
}

export default FlightManifestViewer;
